import { Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import { P } from '@/components/PublicLayout';
import { useLang, useT } from '@/i18n/LanguageContext';
import { nieuwWerkContent } from '@/i18n/content/nieuwwerk';
import { ui } from '@/i18n/content/ui';
import { pathFor } from '@/i18n/routes';
import { useArtworks, formatPrice } from '@/hooks/useArtworks';
import { useTriggerStream } from '@/hooks/useTriggerStream';
import { CumulativeConstellationClock } from '@/components/viz/CumulativeConstellationClock';
import { useTheme } from '@/contexts/ThemeContext';

export default function NieuwWerk() {
  const lang = useLang();
  const t = useT(nieuwWerkContent);
  const labels = ui[lang];
  const { artworks, loading } = useArtworks();
  const { triggers } = useTriggerStream('10y', true);
  const { isDark } = useTheme();

  return (
    <>
      <Seo title={t.seoTitle} description={t.seoDescription} path={pathFor('nieuwwerk', lang)} />

      <div className="max-w-3xl mx-auto px-6 pt-16 pb-8">
        <h1 className="text-3xl md:text-5xl font-light tracking-tight">{t.introductionTitle}</h1>
      </div>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        {t.introduction.split('\n\n').map((p, i) => (
          <P key={i}>{p}</P>
        ))}

        <div className="my-8 space-y-8">
          <CumulativeConstellationClock triggers={triggers} dark={isDark} hideCaption />
        </div>

        <blockquote className="my-8 border-l-2 border-[var(--viz-accent)] pl-6 italic text-sitefg/70">
          {t.quote.map((line, i) => (
            <span key={i} className={`block ${i === 0 ? 'font-bold not-italic' : ''}`}>
              {line}
            </span>
          ))}
        </blockquote>

        <h2 className="mt-14 mb-4 text-2xl font-light tracking-tight">{t.title}</h2>
        <P>{t.intro}</P>


        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {loading && (
            <p className="py-16 text-center text-sitefg/40 col-span-full">{labels.loading}</p>
          )}

          {!loading && artworks.length === 0 && (
            <p className="py-16 text-center text-sitefg/40 col-span-full">{labels.noWorks}</p>
          )}

          {artworks.map((a) => {
            const title = lang === 'nl' ? a.title_nl : a.title_en;
            const price = formatPrice(a, lang);
            return (
              <Link key={a.id} to={pathFor('nieuwwerk', lang, a.slug)} className="group block">
                <div className="relative aspect-square overflow-hidden rounded-sm bg-sitefg/5 border border-sitefg/5">
                  {a.images[0]?.url ? (
                    <img
                      src={a.images[0].url}
                      alt={a.images[0].alt || title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="h-full w-full" />
                  )}
                  {a.sold && (
                    <span className="absolute top-2 left-2 bg-sitefg/70 text-sitebg text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm">
                      {labels.soldOut}
                    </span>
                  )}
                </div>
                <div className="mt-3">
                  <div className="text-sm text-sitefg/80 group-hover:text-[var(--viz-accent)] transition-colors">
                    {title}
                    {a.year ? `, ${a.year}` : ''}
                  </div>
                  <div className="text-xs font-mono tracking-wider text-sitefg/40 mt-1">
                    {a.sold ? labels.soldOut : price ?? labels.onRequest}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </main>
    </>
  );
}
